<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <h1>Admin Dashboard</h1>
      <v-btn
        data-test="refresh-button"
        color="primary"
        @click="refreshData"
        :loading="loading"
      >
        <v-icon left>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !dashboardData" data-test="loading-spinner" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="64" />
    </div>

    <!-- Error State -->
    <v-alert
      v-if="error"
      data-test="error-alert"
      type="error"
      dismissible
      @click:close="error = null"
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <!-- Dashboard Content -->
    <v-container v-if="dashboardData && !loading" fluid>
      <v-row>
        <!-- Total Users -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card data-test="metric-card" class="metric-card">
            <v-card-text>
              <div class="metric-value" data-test="total-users">
                {{ formatNumber(dashboardData.total_users) }}
              </div>
              <div class="metric-label">Total Users</div>
              <v-icon class="metric-icon" color="primary">mdi-account-group</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Active Users Today -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card data-test="metric-card" class="metric-card">
            <v-card-text>
              <div class="metric-value" data-test="active-users">
                {{ formatNumber(dashboardData.active_users_today) }}
              </div>
              <div class="metric-label">Active Users Today</div>
              <v-icon class="metric-icon" color="success">mdi-account-check</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Total Prescriptions -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card data-test="metric-card" class="metric-card">
            <v-card-text>
              <div class="metric-value" data-test="total-prescriptions">
                {{ formatNumber(dashboardData.total_prescriptions) }}
              </div>
              <div class="metric-label">Total Prescriptions</div>
              <v-icon class="metric-icon" color="info">mdi-pill</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Adherence Rate -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card data-test="metric-card" class="metric-card">
            <v-card-text>
              <div class="metric-value" data-test="adherence-rate">
                {{ formatPercentage(dashboardData.adherence_rate) }}
              </div>
              <div class="metric-label">Adherence Rate</div>
              <v-icon class="metric-icon" color="success">mdi-chart-line</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Total Feedback -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card 
            data-test="metric-card"
            :class="['metric-card', 'clickable', 'feedback-card']"
            @click="navigateTo('/feedback')"
          >
            <v-card-text>
              <div class="metric-value" data-test="total-feedback">
                {{ formatNumber(dashboardData.total_feedback) }}
              </div>
              <div class="metric-label">Total Feedback</div>
              <v-icon class="metric-icon" color="warning">mdi-message-text</v-icon>
              <v-chip 
                v-if="dashboardData.open_feedback > 0"
                class="open-badge"
                color="error"
                small
              >
                {{ dashboardData.open_feedback }} Open
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Open Feedback -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card data-test="metric-card" class="metric-card">
            <v-card-text>
              <div class="metric-value" data-test="open-feedback">
                {{ formatNumber(dashboardData.open_feedback) }}
              </div>
              <div class="metric-label">Open Feedback</div>
              <v-icon class="metric-icon" color="error">mdi-alert-circle</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Feature Flags Active -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card data-test="metric-card" class="metric-card">
            <v-card-text>
              <div class="metric-value" data-test="feature-flags">
                {{ formatNumber(dashboardData.feature_flags_active) }}
              </div>
              <div class="metric-label">Active Feature Flags</div>
              <v-icon class="metric-icon" color="purple">mdi-toggle-switch</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Newsletter Subscribers -->
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card data-test="metric-card" class="metric-card">
            <v-card-text>
              <div class="metric-value" data-test="newsletter-subscribers">
                {{ formatNumber(dashboardData.newsletter_subscribers) }}
              </div>
              <div class="metric-label">Newsletter Subscribers</div>
              <v-icon class="metric-icon" color="indigo">mdi-email-newsletter</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Service Health Summary -->
        <v-col cols="12">
          <v-card 
            data-test="metric-card"
            :class="['metric-card', 'clickable', 'health-card']"
            @click="navigateTo('/health')"
          >
            <v-card-title>Service Health Summary</v-card-title>
            <v-card-text>
              <div data-test="health-summary" class="health-summary">
                <v-chip color="success" class="ma-1">
                  {{ dashboardData?.services_health_summary?.healthy || '0' }} Healthy
                </v-chip>
                <v-chip color="warning" class="ma-1">
                  {{ dashboardData?.services_health_summary?.degraded || '0' }} Degraded
                </v-chip>
                <v-chip color="error" class="ma-1">
                  {{ dashboardData?.services_health_summary?.down || '0' }} Down
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminStore } from '@/store/modules/admin'

const router = useRouter()
const adminStore = useAdminStore()

// Computed properties
const dashboardData = computed(() => adminStore.dashboardData)
const loading = computed(() => adminStore.loading)
const error = computed(() => adminStore.error)

// Methods
const refreshData = async () => {
  await adminStore.fetchDashboardData()
}

const formatNumber = (num) => {
  if (!num && num !== 0) return '0'
  return new Intl.NumberFormat('en-US').format(num)
}

const formatPercentage = (num) => {
  if (!num && num !== 0) return '0%'
  return `${num.toFixed(1)}%`
}

const navigateTo = (path) => {
  router.push(path)
}

// Lifecycle
onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.dashboard {
  padding: 24px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.metric-card {
  position: relative;
  transition: transform 0.2s;
}

.metric-card.clickable {
  cursor: pointer;
}

.metric-card.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.metric-value {
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1.2;
  color: #1976d2;
}

.metric-label {
  font-size: 0.875rem;
  color: #666;
  margin-top: 4px;
}

.metric-icon {
  position: absolute;
  top: 16px;
  right: 16px;
  opacity: 0.2;
  font-size: 48px;
}

.open-badge {
  position: absolute;
  bottom: 16px;
  right: 16px;
}

.health-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
</style>